import type { BlockMetadata } from '@visualizer/types';
import { SOLAR_MONTH_TO_FRAME } from '@gui/constants/SolarMonths';
import { getGlobalState } from '@gui/store/persistent/persistentStore';
import { getGrid } from 'store/LoadingStore';

type TargetLayerInfo = {
    layerId: string;
    layerName: string;
    frame?: number;
};

type FilterTargetError = {
    message: string;
};

function resolveTargetFrame(targetLayer: NonNullable<BlockMetadata['targetLayer']>): number | undefined {
    if (targetLayer.frame === 'heatingPeaks') {
        const month = getGlobalState().solarEnergyResources.energyDemand.heatingPeakMonth;
        return SOLAR_MONTH_TO_FRAME[month as keyof typeof SOLAR_MONTH_TO_FRAME] ?? 0;
    }
    if (typeof targetLayer.frame === 'number') return targetLayer.frame;

    return undefined;
}

export function resolveTargetLayer(metadata: BlockMetadata | undefined): {
    target?: TargetLayerInfo;
    selection?: TargetLayerInfo[];
    error?: FilterTargetError;
} {
    const targetLayer = metadata?.targetLayer;
    if (!targetLayer) return {};

    const candidates = getGrid().findLayersByKind(targetLayer.simKind);
    const filteredCandidates =
        targetLayer.frameCount !== undefined
            ? candidates.filter((layer) => layer.getFrameCollection().length === targetLayer.frameCount)
            : candidates;

    // Error target layer doesn't exist:
    if (filteredCandidates.length === 0) {
        return { error: { message: `Cannot apply "${metadata.name}": target layer does not exist.` } };
    }

    // A single target layer found, good:
    const frame = resolveTargetFrame(targetLayer);
    if (filteredCandidates.length === 1) {
        return {
            target: {
                layerId: filteredCandidates[0].getUID(),
                layerName: filteredCandidates[0].name,
                frame,
            },
        };
    }

    // Multiple possible target layers found, return a selection:
    return {
        selection: filteredCandidates.map((layer) => ({
            layerId: layer.getUID(),
            layerName: layer.name,
            frame,
        })),
    };
}

export type { TargetLayerInfo as FilterTargetResolution, FilterTargetError };
